import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, ConfirmDialog, Drawer, EmptyState, Input, Select, Spinner, Switch, useToast } from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useProperty } from '../../app/PropertyContext';
import { catalogApi, LOCATION_KINDS } from '../catalog/catalogApi';
import '../catalog/catalog.css';

const EMPTY = { name: '', kind: 'store', propertyId: '', isActive: true };

export default function LocationsTab() {
  const toast = useToast();
  const t = useT();
  const { properties, hasPermission } = useProperty();
  const canManage = hasPermission('settings.properties.manage');

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const propName = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p.name])), [properties]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLocations(await catalogApi.listLocations());
    } catch (err) {
      toast.error(t('Could not load locations'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDrawerOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ name: l.name ?? '', kind: l.kind ?? 'store', propertyId: l.propertyId ?? '', isActive: l.isActive ?? true });
    setDrawerOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), kind: form.kind, propertyId: form.propertyId || null, isActive: form.isActive };
      if (editing) await catalogApi.updateLocation(editing.id, { id: editing.id, tableViewOutletId: editing.tableViewOutletId ?? null, ...payload });
      else await catalogApi.createLocation(payload);
      toast.success(editing ? t('Location updated') : t('Location created'));
      setDrawerOpen(false);
      await load();
    } catch (err) {
      toast.error(t('Could not save location'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removeLocation(deleteTarget.id);
      toast.success(t('Location deleted'));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(t('Could not delete location'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  const propertyOptions = [{ value: '', label: t('— Organization-wide —') }, ...properties.map((p) => ({ value: p.id, label: p.name }))];

  return (
    <>
      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('New location')}</Button>}
      </div>

      {locations.length === 0 ? (
        <EmptyState
          icon={<Icon.MapPin size={28} />}
          title={t('No locations yet')}
          description={t('Add storage locations (warehouses, walk-ins, bars, kitchens). Inventory and par levels are tracked per location.')}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('New location')}</Button> : null}
        />
      ) : (
        <div className="cat-list">
          {locations.map((l) => (
            <div className="cat-row" key={l.id}>
              <div className="cat-row__main">
                <span className="cat-row__name">
                  {l.name}
                  {!l.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
                </span>
                <span className="cat-row__meta">
                  {t(LOCATION_KINDS.find((k) => k.value === l.kind)?.label || l.kind)}
                  {l.propertyId && propName[l.propertyId] ? ` · ${propName[l.propertyId]}` : ` · ${t('Organization-wide')}`}
                </span>
              </div>
              {canManage && (
                <div className="cat-row__actions">
                  <IconButton aria-label={t('Edit location')} variant="ghost" size="icon-sm" onClick={() => openEdit(l)}><Icon.Pencil size={15} /></IconButton>
                  <IconButton aria-label={t('Delete location')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(l)}><Icon.Trash size={15} /></IconButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Drawer open={drawerOpen} onClose={() => (submitting ? null : setDrawerOpen(false))} title={editing ? t('Edit location') : t('New location')} size="sm">
        <form className="pp-form" onSubmit={save}>
          <Input label={t('Name')} placeholder={t('Main Warehouse')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
          <div className="ps-field">
            <span className="ps-field__label">{t('Type')}</span>
            <Select value={form.kind} onChange={(v) => setForm((f) => ({ ...f, kind: v }))} options={LOCATION_KINDS.map((k) => ({ ...k, label: t(k.label) }))} ariaLabel={t('Type')} />
          </div>
          <div className="ps-field">
            <span className="ps-field__label">{t('Property')}</span>
            <Select value={form.propertyId} onChange={(v) => setForm((f) => ({ ...f, propertyId: v }))} options={propertyOptions} ariaLabel={t('Property')} searchable />
          </div>
          <Switch checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label={t('Active')} />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setDrawerOpen(false)} disabled={submitting}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={submitting}>{editing ? t('Save changes') : t('Create location')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete location')}
        description={deleteTarget ? t('Delete "{name}"?', { name: deleteTarget.name }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
